import { useState } from 'react'
import { X, TrendingUp, TrendingDown, AlertCircle, CheckCircle2 } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatCurrency, formatPercent, cn } from '@/lib/utils'
import type { StockQuote, TradeType } from '@/types'

interface Props {
  symbol: string
  quote: StockQuote
  defaultType?: TradeType
  currentShares?: number
  onClose: () => void
  onSuccess: (newBalance: number) => void
}

export default function TradeModal({
  symbol,
  quote,
  defaultType = 'buy',
  currentShares = 0,
  onClose,
  onSuccess,
}: Props) {
  const { user, refreshUser } = useAuth()
  const [tradeType, setTradeType] = useState<TradeType>(defaultType)
  const [shares, setShares] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const sharesNum = parseInt(shares, 10)
  const isValidShares = !isNaN(sharesNum) && sharesNum > 0
  const totalCost = isValidShares ? sharesNum * quote.price : 0

  const canAfford = user ? user.virtual_balance >= totalCost : false
  const canSell = currentShares >= sharesNum && sharesNum > 0

  const isPositive = quote.changePercent >= 0

  const handleMaxShares = () => {
    if (tradeType === 'buy' && user) {
      const max = Math.floor(user.virtual_balance / quote.price)
      setShares(String(max))
    } else if (tradeType === 'sell') {
      setShares(String(currentShares))
    }
  }

  const handleTrade = async () => {
    if (!isValidShares) return
    setIsLoading(true)
    setError(null)

    const res = await api.executeTrade(symbol, tradeType, sharesNum)
    setIsLoading(false)

    if (res.success && res.data) {
      setSuccess(
        tradeType === 'buy'
          ? `Bought ${sharesNum} shares of ${symbol} at ${formatCurrency(quote.price)}`
          : `Sold ${sharesNum} shares of ${symbol} at ${formatCurrency(quote.price)}`
      )
      await refreshUser()
      onSuccess(res.data.newBalance)
    } else {
      setError(res.error ?? 'Trade failed')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-[#1e1f23] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06]">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold">{symbol}</span>
              <span
                className={cn(
                  'text-xs px-2 py-0.5 rounded-full font-medium',
                  isPositive ? 'bg-gain-muted text-gain' : 'bg-loss-muted text-loss'
                )}
              >
                {isPositive ? '+' : ''}{quote.changePercent.toFixed(2)}%
              </span>
            </div>
            <div className="text-2xl font-semibold font-mono mt-0.5">
              {formatCurrency(quote.price)}
            </div>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="px-6 py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-gain mx-auto mb-3" />
            <div className="text-sm text-white/60">{success}</div>
            <button onClick={onClose} className="mt-6 btn-primary w-full">
              Done
            </button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Trade type toggle */}
            <div className="flex gap-2">
              {(['buy', 'sell'] as TradeType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setTradeType(type)
                    setShares('')
                    setError(null)
                  }}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all',
                    tradeType === type
                      ? type === 'buy'
                        ? 'bg-gain text-black'
                        : 'bg-loss text-white'
                      : 'bg-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.1]'
                  )}
                >
                  {type === 'buy' ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {/* Shares input */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs text-white/40 font-medium uppercase tracking-wider">
                  Shares
                </label>
                <button
                  onClick={handleMaxShares}
                  className="text-xs text-accent hover:underline"
                >
                  {tradeType === 'buy' ? 'Max buyable' : `Max (${currentShares})`}
                </button>
              </div>
              <input
                type="number"
                min="1"
                step="1"
                value={shares}
                onChange={(e) => {
                  setShares(e.target.value)
                  setError(null)
                }}
                placeholder="0"
                className="input-base w-full text-right font-mono text-lg"
              />
            </div>

            {/* Order summary */}
            <div className="bg-white/[0.04] rounded-xl px-4 py-3 space-y-2 text-sm">
              <div className="flex justify-between text-white/50">
                <span>Price per share</span>
                <span className="font-mono">{formatCurrency(quote.price)}</span>
              </div>
              <div className="flex justify-between text-white/50">
                <span>Quantity</span>
                <span className="font-mono">{isValidShares ? sharesNum.toLocaleString() : '—'}</span>
              </div>
              <div className="border-t border-white/[0.06] pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span className="font-mono">{isValidShares ? formatCurrency(totalCost) : '—'}</span>
              </div>
            </div>

            {/* Balance info */}
            <div className="flex justify-between text-xs text-white/30">
              <span>Available cash</span>
              <span className="font-mono">{user ? formatCurrency(user.virtual_balance) : '—'}</span>
            </div>

            {error && (
              <div className="flex items-start gap-2 text-loss text-sm bg-loss-muted rounded-lg px-3 py-2.5">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            <button
              onClick={handleTrade}
              disabled={
                !isValidShares ||
                isLoading ||
                (tradeType === 'buy' && !canAfford) ||
                (tradeType === 'sell' && !canSell)
              }
              className={cn(
                'w-full py-3 rounded-xl font-semibold transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed',
                tradeType === 'buy'
                  ? 'bg-gain text-black hover:brightness-110'
                  : 'bg-loss text-white hover:brightness-110'
              )}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Processing...
                </span>
              ) : tradeType === 'buy' ? (
                isValidShares && !canAfford
                  ? 'Insufficient funds'
                  : `Buy ${isValidShares ? sharesNum : ''} ${symbol}`
              ) : (
                isValidShares && !canSell
                  ? `You only own ${currentShares} shares`
                  : `Sell ${isValidShares ? sharesNum : ''} ${symbol}`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
